// Shared theme and styles for AllegroAdsFaktury

export const colors = {
  // Primary colors
  primary: '#2196F3',
  primaryLight: '#e3f2fd',
  primaryDark: '#1565c0',

  // Secondary colors
  secondary: '#4CAF50',
  secondaryLight: '#e8f5e9',
  secondaryDark: '#2e7d32',

  // Status colors
  success: '#4CAF50',
  successLight: '#e8f5e9',
  successDark: '#2e7d32',

  warning: '#FF9800',
  warningLight: '#fff3e0',
  warningDark: '#e65100',

  error: '#f44336',
  errorLight: '#ffebee',
  errorDark: '#c62828',

  info: '#2196F3',
  infoLight: '#e3f2fd',
  infoDark: '#1565c0',

  // Neutral colors
  gray50: '#fafafa',
  gray100: '#f5f5f5',
  gray200: '#eeeeee',
  gray300: '#e0e0e0',
  gray400: '#bdbdbd',
  gray500: '#9e9e9e',
  gray600: '#757575',
  gray700: '#616161',
  gray800: '#424242',
  gray900: '#212121',

  // Background
  background: '#ffffff',
  backgroundAlt: '#f9f9f9',

  // Text
  textPrimary: '#212121',
  textSecondary: '#666666',
  textDisabled: '#9e9e9e',

  // Borders
  border: '#ddd',
  borderLight: '#eee',
  borderDark: '#ccc',
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
};

export const borderRadius = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  full: 9999,
};

export const shadows = {
  sm: '0 1px 2px rgba(0,0,0,0.05)',
  md: '0 2px 4px rgba(0,0,0,0.1)',
  lg: '0 4px 8px rgba(0,0,0,0.12)',
  xl: '0 8px 16px rgba(0,0,0,0.15)',
};

export const typography = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: {
    xs: 11,
    sm: 12,
    base: 14,
    md: 15,
    lg: 16,
    xl: 18,
    xxl: 20,
    xxxl: 24,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

// Common component styles
export const styles = {
  // Card
  card: {
    background: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.lg,
    boxShadow: shadows.md,
  },

  // Button primary
  buttonPrimary: {
    padding: `${spacing.sm}px ${spacing.lg}px`,
    background: colors.primary,
    color: '#ffffff',
    border: 'none',
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
  },

  // Button secondary
  buttonSecondary: {
    padding: `${spacing.sm}px ${spacing.lg}px`,
    background: colors.background,
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontWeight: typography.fontWeight.normal,
    fontSize: typography.fontSize.base,
  },

  // Button danger
  buttonDanger: {
    padding: `${spacing.sm}px ${spacing.lg}px`,
    background: colors.errorLight,
    color: colors.errorDark,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
  },

  // Input
  input: {
    padding: `${spacing.sm}px ${spacing.md}px`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily,
    width: '100%',
  },

  // Select
  select: {
    padding: `${spacing.sm}px ${spacing.md}px`,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily,
    background: colors.background,
    cursor: 'pointer',
  },

  // Badge
  badge: {
    padding: `${spacing.xs}px ${spacing.sm}px`,
    borderRadius: borderRadius.full,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    display: 'inline-block',
  },

  // Table
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    boxShadow: shadows.md,
  },

  tableHeader: {
    padding: spacing.md,
    textAlign: 'left' as const,
    borderBottom: `2px solid ${colors.border}`,
    fontWeight: typography.fontWeight.semibold,
    background: colors.gray100,
    fontSize: typography.fontSize.base,
  },

  tableCell: {
    padding: spacing.md,
    borderBottom: `1px solid ${colors.borderLight}`,
    fontSize: typography.fontSize.base,
  },

  // Container
  container: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: spacing.xl,
  },

  // Section
  section: {
    marginBottom: spacing.xxl,
    padding: spacing.lg,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    background: colors.backgroundAlt,
  },
};

// Helper functions
export const createButtonStyle = (variant: 'primary' | 'secondary' | 'danger' | 'success' | 'warning', disabled = false) => {
  const baseStyle = {
    padding: `${spacing.sm}px ${spacing.lg}px`,
    border: 'none',
    borderRadius: borderRadius.sm,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.2s ease',
  };

  switch (variant) {
    case 'primary':
      return { ...baseStyle, background: colors.primary, color: '#ffffff' };
    case 'secondary':
      return { ...baseStyle, background: colors.background, color: colors.textPrimary, border: `1px solid ${colors.border}` };
    case 'danger':
      return { ...baseStyle, background: colors.errorLight, color: colors.errorDark, border: `1px solid ${colors.error}` };
    case 'success':
      return { ...baseStyle, background: colors.success, color: '#ffffff' };
    case 'warning':
      return { ...baseStyle, background: colors.warning, color: '#ffffff' };
  }
};

export const createBadgeStyle = (variant: 'success' | 'error' | 'warning' | 'info' | 'default') => {
  const baseStyle = {
    padding: `${spacing.xs}px ${spacing.sm}px`,
    borderRadius: borderRadius.full,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    display: 'inline-block' as const,
  };

  switch (variant) {
    case 'success':
      return { ...baseStyle, background: colors.successLight, color: colors.successDark };
    case 'error':
      return { ...baseStyle, background: colors.errorLight, color: colors.errorDark };
    case 'warning':
      return { ...baseStyle, background: colors.warningLight, color: colors.warningDark };
    case 'info':
      return { ...baseStyle, background: colors.infoLight, color: colors.infoDark };
    case 'default':
      return { ...baseStyle, background: colors.gray100, color: colors.gray700 };
  }
};
